import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaSpinner } from 'react-icons/fa';
import { ResultCard, BatchCard, groupResultsByBatch } from '../../../components/ListingScore/bulk_listing_score/ListingScoreResultHistory';
import { navigateToSingleListingUpgrade } from '../../../utils/upgradeListingNavigation';
import listingScoreHistoryStyles from '../../../components/ListingScore/bulk_listing_score/ListingScoreResultHistory.module.css';
import styles from '../ManagePage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ListingScoreSection = ({ token }) => {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [bulkResults, setBulkResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bulkError, setBulkError] = useState(null);

  const fetchListingScoreHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/listing_score/single-history`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.data?.success && Array.isArray(response.data.results)) {
        setResults(response.data.results);
      } else {
        setResults([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load listing scores');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchBulkListingScoreHistory = useCallback(async () => {
    if (!token) return;
    setBulkLoading(true);
    setBulkError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/listing_score/all-scores`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.data?.success && Array.isArray(response.data.results)) {
        const sorted = [...response.data.results].sort((a, b) => {
          const da = a.completed_at ? new Date(a.completed_at) : new Date(0);
          const db = b.completed_at ? new Date(b.completed_at) : new Date(0);
          return db - da;
        });
        setBulkResults(sorted);
      } else {
        setBulkResults([]);
      }
    } catch (err) {
      setBulkError(err.response?.data?.error || err.message || 'Failed to load bulk listing scores');
      setBulkResults([]);
    } finally {
      setBulkLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchListingScoreHistory();
  }, [fetchListingScoreHistory]);

  useEffect(() => {
    fetchBulkListingScoreHistory();
  }, [fetchBulkListingScoreHistory]);

  const batches = useMemo(() => groupResultsByBatch(bulkResults), [bulkResults]);
  const hasBatches = batches.length > 0;

  const handleBatchClick = useCallback((batch) => {
    navigate('/dashboard/listing-score-results', { state: { selectedBatchId: batch.task_id } });
  }, [navigate]);

  const handleViewListingScore = useCallback((asin, scoreData) => {
    if (!scoreData || !scoreData.has_score) return;
    const analysis = scoreData.analysis || {};
    const productInfo = scoreData.product_info || {};
    const transformedData = {
      asin: asin,
      title: productInfo.title || 'Product Title',
      main_image: productInfo.main_image || '',
      b_plus_images: productInfo.b_plus_images || productInfo.all_images || [],
      description: productInfo.description || '',
      bullet_points: productInfo.bullet_points || [],
      specifications: productInfo.specifications || [],
      a_plus_content: productInfo.a_plus_content || false,
      analysis: {
        total_score: scoreData.total_score || 0,
        percentage_score: scoreData.score || 0,
        overall_analysis: analysis.overall_analysis || '',
        component_scores: analysis.component_scores || {},
        competitive_advantages: analysis.competitive_advantages || [],
        improvement_areas: analysis.improvement_areas || [],
        critical_gaps: analysis.critical_gaps || [],
        recommendations: analysis.recommendations || [],
      },
      product_info: productInfo,
    };
    navigate('/dashboard/listing-score-detail', { state: { selectedScoreData: transformedData } });
  }, [navigate]);

  const loadingAny = loading || bulkLoading;
  const errorAny = error || bulkError;
  const hasSingle = results.length > 0;
  const hasAny = hasBatches || hasSingle;

  const combinedItems = useMemo(() => {
    const batchItems = batches.map((batch) => ({
      type: 'batch',
      key: `batch-${batch.task_id}`,
      date: batch.completed_at ? new Date(batch.completed_at) : new Date(0),
      batch,
    }));
    const singleItems = results.map((result, index) => ({
      type: 'single',
      key: `single-${result.asin}-${result.created_at || index}`,
      date: result.created_at ? new Date(result.created_at) : new Date(0),
      result,
      index,
    }));
    return [...batchItems, ...singleItems].sort((a, b) => b.date - a.date);
  }, [batches, results]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderTitle}>
          <span className={styles.allProjectsTitle}>Listing Score Results</span>
        </div>
      </div>
      {loadingAny && (
        <div className={styles.loading}><FaSpinner className={styles.spinner} /></div>
      )}
      {errorAny && !loadingAny && (
        <div className={styles.error}>{errorAny}</div>
      )}
      {!loadingAny && !errorAny && !hasAny && (
        <div className={styles.emptyState}>
          <h3>No listing score results</h3>
          <p>Run a single or bulk listing score analysis to see results here.</p>
        </div>
      )}
      {!loadingAny && !errorAny && hasAny && (
        <div className={listingScoreHistoryStyles.listingScoreRow}>
          {combinedItems.map((item) =>
            item.type === 'batch' ? (
              <BatchCard
                key={item.key}
                batch={item.batch}
                onClick={() => handleBatchClick(item.batch)}
              />
            ) : (
              <ResultCard
                key={item.key}
                result={item.result}
                index={item.index}
                onViewScore={handleViewListingScore}
                onUpgrade={(productData) => navigateToSingleListingUpgrade(navigate, productData)}
              />
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ListingScoreSection;
